import {
  COMPONENT,
  GROUP,
  LABEL,
  SHAPE,
  INPUT,
  LIST,
  IMAGE_UPLOAD,
  FILE_UPLOAD,
  DATE_PICKER,
  LIBRARY_COMPONENT,
  SELECT,
  LOCATION_INPUT,
  dataTypes,
} from '@adalo/constants'

import {
  insert,
  update,
  remove,
  evaluate,
  getBoundingBox,
  getDropPath,
  getGroupPath,
  getInsertPath,
  getObject,
  incrementPath,
  decrementPath,
  isChildPath,
  joinPaths,
  remapSiblings,
  removeChildren,
  subPath,
  getComponentPaths,
  pathLength,
  nextPath,
  translateChildren,
  updateBounds,
  updateParentBounds,
  mergeReducers,
  getId,
  uniqueElements,
  sortPaths,
  deepMerge,
  getParentPath,
} from '@adalo/utils'

import { setGlobalDefaults } from 'utils/library-globals'
import { defaults, convertType, indexByType } from 'utils/objects'

import {
  updateComponentBounds,
  getAppComponent,
  hasRole,
} from 'utils/libraries'
import { saveTouched, saveDiffed, setPrevList } from 'utils/saving'
import { requestApp, saveLibraryGlobals } from 'utils/io'
import { createName } from 'utils/naming'
import { updateObjectWidth } from 'utils/text'
import { getSnapGrid } from 'utils/snapping'
import { calculateZoom, getOffset } from 'utils/zoom'
import { updateOptions, updateParentOptions } from 'utils/groupTypes'
import { convertToInput } from 'utils/conversion'
import { ADD } from 'utils/tabs'

import { getAccordionState } from 'ducks/accordions'

import selectionReducer, {
  DEFAULT_SELECTION,
  SELECT_PARENT,
  getActiveComponent,
} from './selection'

import { SET_TOOL } from './tools'

import {
  getBestParent,
  getInsertPosition,
  getAbsoluteBbox,
  alignToRect,
} from '../../utils/geometry'

import textEditingReducer from './textEditing'
import shapeEditingReducer from './shapeEditing'
import sketchUploadReducer from './sketchUpload'
import positioningReducer from './positioning'
import clipboardReducer from './clipboard'
import snappingReducer, { snappingMiddleware } from './snapping'
import tabsReducer from './tabs'

import { defaultListChildren } from '../../utils/lists'

export const REQUEST_DATA = Symbol('REQUEST_DATA')
export const SET_DATA = Symbol('SET_DATA')
export const CREATE_OBJECT = Symbol('CREATE_OBJECT')
export const UPDATE_OBJECT = Symbol('UPDATE_OBJECT')
export const UPDATE_OBJECTS = Symbol('UPDATE_OBJECTS')
export const CHANGE_OBJECT_TYPE = Symbol('CHANGE_OBJECT_TYPE')
export const RESIZE_OBJECT = Symbol('RESIZE_OBJECT')
export const POSITION_OBJECTS = Symbol('POSITION_OBJECTS')
export const DELETE_OBJECT = Symbol('DELETE_OBJECT')
export const REORDER_OBJECTS = Symbol('REORDER_OBJECTS')
export const REORDER_OBJECTS_MOVE_LAST = Symbol('REORDER_OBJECTS_MOVE_LAST')
export const REORDER_OBJECTS_MOVE_FIRST = Symbol('REORDER_OBJECTS_MOVE_FIRST')
export const REORDER_OBJECTS_MOVE_UP = Symbol('REORDER_OBJECTS_MOVE_UP')
export const REORDER_OBJECTS_MOVE_DOWN = Symbol('REORDER_OBJECTS_MOVE_DOWN')
export const GROUP_OBJECTS = Symbol('GROUP_OBJECTS')
export const GROUP_OBJECTS_TO_LIST = Symbol('GROUP_OBJECTS_TO_LIST')
export const UNGROUP_OBJECTS = Symbol('UNGROUP_OBJECTS')
export const SET_PAGE_SIZE = Symbol('SET_PAGE_SIZE')
export const ALIGN_OBJECTS = Symbol('ALIGN_OBJECTS')
const ZOOM = Symbol('ZOOM')
const RESET_ZOOM = Symbol('RESET_ZOOM')
const PAN = Symbol('PAN')
const SET_LIBRARY_GLOBAL = Symbol('SET_LIBRARY_GLOBAL')

const INITIAL_STATE = {
  appId: null,
  loading: false,
  name: null,
  zoom: {
    scale: 1,
    offset: [0, 0],
  },
  map: {},
  parentMap: {},
  list: [],

  // Indices map to components at the top level
  // typeIndex: { COMPONENT1: { TYPE: [...] } }
  // Others: { COMPONENT1: [...] }
  typeIndex: {},
  selection: DEFAULT_SELECTION,
  hoverSelection: DEFAULT_SELECTION,
  activeComponent: null,
  textEditing: null,
  shapeEditing: null,
  draggingOutControl: false,
  draggingInControl: false,
  selectedPoint: null,
  draggingSelectedPoint: false,
  zoomAppId: null,
  dragging: false,
  panning: false,
  xGrid: null,
  yGrid: null,
  currentXSnap: null,
  currentYSnap: null,
  positioningObjects: null,
  positioningStartPoint: null,
  positioningConstraint: null,
  activeTab: null,
  sketchUpload: {
    uploadInProgress: false,
    uploadError: null,
    progress: null,
  },
  selectedParent: null,
  libraryGlobals: {},
}

const emptyObject = (obj, list, setId = true) => {
  const id = setId ? getId() : obj.id

  const newObj = {
    ...defaults[obj.type],
    ...obj,
    name: createName(obj.type, obj.name, list),
    id,
  }

  for (const symbol of Object.getOwnPropertySymbols(newObj)) {
    delete newObj[symbol]
  }

  if (newObj.type !== GROUP && newObj.type !== COMPONENT) {
    newObj.x = newObj.x || 0
    newObj.y = newObj.y || 0
  }

  return newObj
}

const getComponentObject = (components, id) => {
  const componentObj = components[id]

  const {
    x,
    y,
    width,
    height,
    objects,
    name,
    backgroundColor,
    backgroundImage,
    backgroundPositionX,
    backgroundPositionY,
    backgroundSize,
    statusBarStyle,
    reverseScroll,
    reusableComponent,
    onVisit,
    componentActions,
    screenTemplate,
  } = componentObj

  return {
    id,
    name,
    backgroundColor,
    backgroundImage,
    backgroundPositionX,
    backgroundPositionY,
    backgroundSize,
    onVisit,
    componentActions,
    type: COMPONENT,
    x: x || 0,
    y: y || 0,
    width,
    height,
    reusableComponent,
    statusBarStyle,
    reverseScroll,
    screenTemplate,
    children: objects,
  }
}

export const resizeParent = obj => {
  return (
    obj.type !== COMPONENT &&
    (obj.type !== LIST ||
      obj.width === 0 ||
      obj.height === 0 ||
      obj.width === Infinity)
  )
}

export const getParentId = (list, map, typeIndex, newObject) => {
  let listIds = []

  list.forEach(component => {
    listIds = listIds.concat((typeIndex[component.id] || {})[LIST] || [])
  })

  listIds = listIds.filter(
    listId =>
      listId !== newObject.id &&
      (!map[newObject.id] || !isChildPath(map[newObject.id] || '', map[listId]))
  )

  const parentId = getBestParent(
    newObject,
    list.concat(
      ...listIds.map(id => {
        return getAbsoluteBbox(getObject(list, map[id]), list, map)
      })
    )
  )

  return parentId
}

const innerPerformCreate = (
  object,
  list,
  objects,
  id,
  newIds,
  state,
  parentId,
  map,
  path,
  isCopy,
  isRelative,
  setSelection,
  zoom,
  selection,
  objectIds,
  newObjects,
  changeIds = true
) => {
  // Not sure why eslint doesn't like this...it is used below
  // eslint-disable-next-line no-unused-vars
  let componentId

  let newObject = emptyObject(object, list, changeIds)

  if (objects.length === 1 && id) {
    newObject.id = id
  }

  newIds.push(newObject.id)

  if (!parentId) {
    parentId = getParentId(list, map, state.typeIndex, newObject)
  }

  let newPath = getInsertPath(list, path, map[parentId])

  if (isCopy && path) {
    newPath = path
    path = nextPath(path)
  }

  if (newObject.type === COMPONENT && pathLength(newPath) > 1) {
    newPath = nextPath(subPath(newPath, 1))
  }

  if (parentId) {
    const component = getObject(list, subPath(map[parentId], 1))
    componentId = component.id

    if (!isRelative) {
      newObject.x -= component.x
      newObject.y -= component.y
    }
  } else {
    // Get out of here if trying to insert non-component at top-level
    if (newObject.type !== COMPONENT) {
      return null
    }

    if (
      !isRelative &&
      !(newObject.x !== undefined || newObject.y !== undefined)
    ) {
      const position = getInsertPosition(list)
      newObject = { ...newObject, ...position }
    }

    if (setSelection) {
      zoom = calculateZoom(newObject)
    }

    componentId = newObject.id
  }

  newObject = updateObjectWidth(newObject)
  list = insert(list, newPath, newObject)
  map = remapSiblings(list, map, newPath)
  list = updateParentBounds(list, map, newObject.id, null, resizeParent)
  list = updateOptions(list, map, newObject.id)

  if (newObject.type !== COMPONENT) {
    selection.push(newObject.id)
  }

  if (
    newObject.type === COMPONENT &&
    newObject.objects &&
    newObject.objects.length > 0
  ) {
    const children = newObject.objects
    newObject.objects = []

    children.forEach(child => {
      const result = innerPerformCreate(
        child,
        list,
        [],
        null,
        newIds,
        state,
        newObject.id,
        map,
        path,
        isCopy,
        true,
        setSelection,
        zoom,
        [],
        objectIds,
        newObjects,
        changeIds
      )

      ;({ list, map } = result)
    })
  }

  if (
    newObject.type === LIST &&
    !isCopy &&
    (!newObject.children || newObject.children.length === 0)
  ) {
    defaultListChildren.forEach(child => {
      child = {
        ...child,
        x: newObject.x + child.x,
        y: newObject.y + child.y,
        id: getId(),
      }

      const result = innerPerformCreate(
        child,
        list,
        [],
        null,
        newIds,
        state,
        newObject.id,
        map,
        path,
        isCopy,
        true,
        setSelection,
        zoom,
        [],
        objectIds,
        newObjects,
        changeIds
      )

      ;({ list, map } = result)
    })
  }

  objectIds.push(newObject.id)
  newObjects.push(newObject)

  return { list, parentId, map, path, zoom }
}

export const performCreate = (
  state,
  path,
  objects,
  id,
  parentId,
  isRelative = false,
  isCopy = false,
  setSelection = true,
  changeIds = true
) => {
  let { list, map, zoom } = state
  const objectIds = []
  const selection = []
  const newObjects = []
  const newIds = []

  if (!Array.isArray(objects)) {
    objects = [objects]
  }

  if (!parentId && state.selectedParent) {
    parentId = state.selectedParent
  }

  state = {
    ...state,
    selectedParent: null,
  }

  for (const object of objects) {
    const result = innerPerformCreate(
      object,
      list,
      objects,
      id,
      newIds,
      state,
      parentId,
      map,
      path,
      isCopy,
      isRelative,
      setSelection,
      zoom,
      selection,
      objectIds,
      newObjects,
      changeIds
    )

    if (result == null) {
      // Get out of here if trying to insert non-component at top-level
      return state
    }

    ;({ list, map, path, zoom } = result)
  }

  const libraryGlobals = setGlobalDefaults(state.libraryGlobals, newObjects)

  saveTouched(
    state.appId,
    list,
    map,
    newObjects.map(o => o.id),
    undefined,
    undefined,
    libraryGlobals
  )

  const newObject = newObjects.length === 1 ? newObjects[0] : {}

  return [
    updateIndices({
      ...state,
      list,
      map,
      zoom,
      libraryGlobals,
      selection: setSelection ? selection : state.selection,
      activeComponent: getActiveComponent(list, map, selection),
      shapeEditing: newObject.type === SHAPE && newObject.id,
    }),
    ...newIds,
  ]
}

export const performDelete = (state, id) => {
  if (!Array.isArray(id)) {
    id = [id]
  }

  let list = state.list
  let map = state.map
  const paths = []
  const componentIds = []

  id.forEach(id => {
    const path = map[id]
    const obj = getObject(list, path)

    if (obj.type === COMPONENT) {
      componentIds.push(id)
    } else {
      paths.push(path)
    }

    list = remove(list, path)

    list = updateParentBounds(list, map, id, null, resizeParent)
    list = updateParentOptions(list, map, id)

    map = { ...map }
    delete map[id]
    map = remapSiblings(list, map, subPath(path, path.length - 1))

    const pieces = path.split('.')
    const parentPath = pieces.slice(0, pieces.length - 1).join('.')
    let siblings = []

    if (parentPath === '') {
      siblings = list
    } else {
      const parentObj = getObject(list, parentPath)

      siblings = (parentObj && parentObj.children) || []
    }

    const pathPrefix = parentPath === '' ? '' : `${parentPath}.`

    siblings.forEach((sibling, position) => {
      map[sibling.id] = `${pathPrefix}${position}`
    })
  })

  saveTouched(state.appId, list, map, null, paths, componentIds)

  return updateIndices({
    ...state,
    list,
    map,
    selection: [],
    hoverSelection: [],
    shapeEditing: null,
  })
}

const updateIndices = (state, paths = null) => {
  let componentPaths = Object.keys(state.list)

  if (paths) {
    componentPaths = getComponentPaths(paths.map(id => state.map[id]))
  }

  const typeIndex = { ...state.typeIndex }

  componentPaths.forEach(path => {
    const component = state.list[path]

    typeIndex[component.id] = indexByType(component.children)
  })

  return {
    ...state,
    typeIndex,
  }
}

// REDUCER

export default mergeReducers(
  INITIAL_STATE,
  [snappingMiddleware],
  selectionReducer,
  textEditingReducer,
  shapeEditingReducer,
  sketchUploadReducer,
  positioningReducer,
  clipboardReducer,
  snappingReducer,
  tabsReducer,

  (state, action) => {
    if (action.type === REQUEST_DATA) {
      const { appId } = action

      // Make call to API
      requestApp(appId)

      if (appId === state.appId) {
        return { ...state, loading: true }
      }

      setPrevList(null)

      return {
        ...INITIAL_STATE,
        activeTab: state.activeTab,
        appId,
        loading: true,
      }
    }

    if (action.type === SET_DATA) {
      const { appId, data, app } = action
      const activeTab = state.activeTab || ADD

      const list = Object.keys(data || {}).map(id =>
        getComponentObject(data, id)
      )

      const libraryGlobals = app.libraryGlobals || {}
      const map = remapSiblings(list, {}, '0')

      let zoom = state.zoom

      if (state.zoomAppId !== appId) {
        zoom = calculateZoom(getBoundingBox(list))
      }

      setPrevList(list)

      let newState = updateIndices({
        ...state,
        appId,
        list,
        map,
        zoom,
        activeTab,
        libraryGlobals,
        loading: false,
      })

      newState = {
        ...newState,
        ...getSnapGrid(newState, []),
      }

      return newState
    }

    if (action.type === CREATE_OBJECT) {
      const { path, object, id, silent } = action

      return performCreate(
        state,
        path,
        object,
        id,
        null,
        false,
        false,
        !silent
      )[0]
    }

    if ([UPDATE_OBJECT, RESIZE_OBJECT].indexOf(action.type) !== -1) {
      const { id, object, skipSave, globalState, resetAccordion } = action
      const path = state.map[id]
      if (!path) return state
      const { libraryGlobals } = state

      const oldObject = getObject(state.list, path) || {}
      const changes = evaluate(object, oldObject)

      let newObject = deepMerge(oldObject, changes)

      if (oldObject.type !== COMPONENT) {
        newObject = updateObjectWidth(newObject)
        newObject = translateChildren(newObject, oldObject)
      }

      if (oldObject.type === LIBRARY_COMPONENT) {
        const { libraryName, componentName } = newObject

        const globals = libraryGlobals[libraryName]?.[componentName]

        const openAccordion = resetAccordion
          ? null
          : getAccordionState(globalState, 'library-inspect')

        newObject = updateComponentBounds(newObject, globals, openAccordion)
      }

      if (
        oldObject.type === LABEL &&
        'width' in object &&
        oldObject.width !== object.width
      ) {
        newObject.autoWidth = false
      }

      let list = update(state.list, path, newObject)
      list = updateParentBounds(list, state.map, id, null, resizeParent)
      list = updateOptions(list, state.map, id)

      if (!skipSave) {
        saveTouched(state.appId, list, state.map, [id])
      }

      return {
        ...state,
        list,
      }
    }

    if (action.type === UPDATE_OBJECTS) {
      const { objects, globalState } = action

      let list = state.list

      for (const object of objects) {
        const { id } = object
        const path = state.map[id]

        const oldObject = getObject(state.list, path)
        const changes = evaluate(object, oldObject)

        let newObject = { ...oldObject, ...changes }

        if (oldObject.type !== COMPONENT) {
          newObject = updateObjectWidth(newObject)
          newObject = translateChildren(newObject, oldObject)
        }

        if (oldObject.type === LIBRARY_COMPONENT) {
          const openAccordion = getAccordionState(
            globalState,
            'library-inspect'
          )

          newObject = updateComponentBounds(newObject, null, openAccordion)
        }

        list = update(list, path, newObject)
        list = updateParentBounds(list, state.map, id, null, resizeParent)
        list = updateOptions(list, state.map, id)
      }

      saveTouched(
        state.appId,
        list,
        state.map,
        objects.map(obj => obj.id)
      )

      return {
        ...state,
        list,
      }
    }

    if (action.type === CHANGE_OBJECT_TYPE) {
      const { id, newType } = action

      const path = state.map[id]
      let object = getObject(state.list, path)
      let { selection, map } = state

      if (newType === INPUT) {
        object = convertToInput(object)
      } else if (newType === LIST) {
        let children = [object]

        if (object.type === GROUP) {
          children = object.children || []
        }

        object = emptyObject(
          {
            children,
            height: 400,
            type: LIST,
          },
          state.list
        )

        selection = [object.id]
      } else {
        object = convertType(object, newType)
      }

      let list = update(state.list, path, object)
      map = remapSiblings(list, map, '0')

      if (object.id !== id) {
        list = updateParentBounds(
          list,
          map,
          object.children[0].id,
          null,
          resizeParent
        )
      }

      if (newType === LIST) {
        const { id } = object
        let obj = getObject(list, map[id])
        obj = { ...obj, height: obj.height * 3 }
        list = update(list, map[id], obj)
      }

      saveTouched(state.appId, list, map, [id])

      return updateIndices({
        ...state,
        selection,
        map,
        list,
      })
    }

    if (action.type === POSITION_OBJECTS) {
      const { ids, offset, shouldSave } = action

      const objects = getSelectedSub(state, ids)

      let list = state.list

      objects.forEach(obj => {
        let newObj = {
          ...obj,
          x: obj.x + offset.x,
          y: obj.y + offset.y,
        }

        if (obj.type !== COMPONENT) {
          newObj = translateChildren(newObj, obj)
        }

        list = update(list, state.map[obj.id], newObj)
        list = updateParentBounds(list, state.map, obj.id, null, resizeParent)
      })

      if (shouldSave) {
        saveTouched(state.appId, list, state.map, ids)
      }

      return {
        ...state,
        list,
      }
    }

    if (action.type === ALIGN_OBJECTS) {
      const { selection, appId, map } = state
      let { list } = state
      const { direction } = action
      const objects = selection.map(id => getObject(list, map[id]))
      const absoluteObjects = objects.map(o => getAbsoluteBbox(o, list, map))

      const bbox =
        objects.length === 1
          ? getObject(list, subPath(map[objects[0].id], 1))
          : getBoundingBox(absoluteObjects)

      objects.forEach((obj, i) => {
        let newObj = alignToRect(obj, absoluteObjects[i], bbox, direction)

        if (newObj.type !== COMPONENT) {
          newObj = translateChildren(newObj, obj)
        }

        list = update(list, map[obj.id], newObj)
        list = updateParentBounds(list, map, obj.id, null, resizeParent)
      })

      saveTouched(appId, list, map, selection)

      return {
        ...state,
        list,
      }
    }

    if (action.type === DELETE_OBJECT) {
      const { id } = action

      return performDelete(state, id)
    }

    if (action.type === REORDER_OBJECTS) {
      const { ids, dropTarget, options } = action
      let { list, map } = state

      const { dropAfter, dropInside } = options

      let dropPath = map[dropTarget]

      if (dropInside) {
        const children = getObject(list, dropPath).children

        if (!children) {
          list = update(list, dropPath, {
            ...getObject(list, dropPath),
            children: [],
          })
        }

        const childCount = children ? children.length : 0
        dropPath = joinPaths(dropPath, `${childCount}`)
      }

      if (!dropAfter) {
        dropPath = incrementPath(dropPath)
      }

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(list, map, null, path, resizeParent)
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(list, map, itm.id, null, resizeParent)
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_FIRST) {
      const { ids } = action
      let { list, map } = state

      const actualPath = map[ids]

      let dropPath = `${subPath(actualPath, pathLength(actualPath) - 1)}.9999`

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(list, map, null, path, resizeParent)
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(list, map, itm.id, null, resizeParent)
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_LAST) {
      const { ids } = action
      let { list, map } = state

      const actualPath = map[ids]

      let dropPath = `${subPath(actualPath, pathLength(actualPath) - 1)}.0`

      const lastLayerCheck = actualPath === dropPath

      if (lastLayerCheck) {
        return state
      }

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(list, map, null, path, resizeParent)
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(list, map, itm.id, null, resizeParent)
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_UP) {
      const { ids } = action
      let { list, map } = state

      const actualPath = map[ids]

      if (!actualPath) {
        return state
      }

      let dropPath = incrementPath(actualPath)
      dropPath = incrementPath(dropPath)

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(list, map, null, path, resizeParent)
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(list, map, itm.id, null, resizeParent)
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (action.type === REORDER_OBJECTS_MOVE_DOWN) {
      const { ids } = action
      let { list, map } = state

      const actualPath = map[ids]

      const lastLayerCheck = actualPath === '0.0'

      if (lastLayerCheck) {
        return state
      }

      let dropPath = decrementPath(actualPath)

      let itemPaths = ids.map(id => map[id]).filter(i => i)

      for (let i = 0; i < itemPaths.length; i += 1) {
        const path = itemPaths[i]

        if (isChildPath(path, dropPath)) {
          return state
        }
      }

      itemPaths = sortPaths(itemPaths)
      const items = itemPaths.map(path => getObject(list, path))

      dropPath = getDropPath(dropPath, itemPaths)

      itemPaths
        .slice()
        .reverse()
        .forEach(path => {
          list = remove(list, path)
          list = updateParentBounds(list, map, null, path, resizeParent)
          list = updateParentOptions(list, map, null, path)
        })

      list = insert(list, dropPath, ...items)
      map = remapSiblings(list, map, '0')

      items.forEach(itm => {
        list = updateParentBounds(list, map, itm.id, null, resizeParent)
        list = updateOptions(list, map, itm.id)
      })

      const effectedPaths = itemPaths.concat([dropPath])
      saveTouched(state.appId, list, map, null, effectedPaths)

      return {
        ...state,
        list,
        map,
        hoverSelection: [],
      }
    }

    if (
      action.type === GROUP_OBJECTS ||
      action.type === GROUP_OBJECTS_TO_LIST
    ) {
      let { list, map } = state
      const { ids } = action

      if (ids.length === 0) {
        return state
      }

      let paths = ids.map(id => map[id])
      paths = paths.filter(path => pathLength(path) > 1)
      paths = removeChildren(sortPaths(paths))

      const group = emptyObject(
        {
          type: action.type === GROUP_OBJECTS_TO_LIST ? LIST : GROUP,
          children: [],
          width: 0,
          height: 0,
        },
        state.list
      )

      const groupPath = getGroupPath(paths)

      if (!groupPath) {
        return
      }

      paths.forEach(path => {
        const obj = getObject(list, path)
        group.children.push(obj)
      })

      const pathsReversed = [...paths]
      pathsReversed.reverse()

      pathsReversed.forEach(path => {
        list = remove(list, path)
      })

      list = insert(list, groupPath, group)
      map = remapSiblings(list, map, groupPath)
      list = updateBounds(list, map, groupPath, resizeParent)

      if (action.type === GROUP_OBJECTS_TO_LIST) {
        let group = getObject(list, groupPath)

        group = {
          ...group,
          height: group.height * 3 + (group.rowMargin || 0) * 2,
        }

        list = update(list, groupPath, group)
      }

      const selection = [group.id]

      saveTouched(state.appId, list, map, null, paths)

      return updateIndices({
        ...state,
        list,
        map,
        selection,
        hoverSelection: [],
      })
    }

    if (action.type === UNGROUP_OBJECTS) {
      const { ids } = action
      let { list, map } = state

      const paths = ids.map(id => map[id])
      let objects = paths.map(path => getObject(list, path))

      objects = objects.filter(obj => obj.type === GROUP)

      let selection = []

      objects.forEach(group => {
        const { id, children } = group
        const path = map[id]

        selection = selection.concat(children.map(c => c.id))

        list = remove(list, path)
        list = insert(list, path, ...children)
        map = remapSiblings(list, map, '0')
      })

      saveTouched(state.appId, list, map, null, paths)

      return updateIndices({
        ...state,
        list,
        map,
        selection,
      })
    }

    if (action.type === ZOOM) {
      const { relativeScale, relativeOffset } = action
      let { scale, offset } = action

      const newState = { ...state.zoom }

      if (relativeScale) {
        scale = state.zoom.scale * relativeScale
      }

      if (relativeOffset) {
        const [diffX, diffY] = relativeOffset
        const [x, y] = state.zoom.offset

        offset = [x + diffX, y + diffY]
      }

      if (scale && !offset) {
        const xCenter = (window.innerWidth - 430) / 2 + 430
        const yCenter = (window.innerHeight - 64) / 2 + 64
        const center = [xCenter, yCenter]

        offset = getOffset(scale, center, state.zoom.scale, state.zoom.offset)
      }

      if (scale) {
        newState.scale = scale
      }

      if (offset) {
        newState.offset = offset
      }

      if (newState.scale > 64 || newState.scale < 1.0 / 16) {
        return state
      }

      return {
        ...state,
        zoomAppId: state.appId,
        zoom: newState,
      }
    }

    if (action.type === RESET_ZOOM) {
      const { list } = state
      const zoom = calculateZoom(getBoundingBox(list))

      return { ...state, zoom }
    }

    if (action.type === '@@redux-undo/UNDO') {
      saveDiffed(state.appId, state.list)
    } else if (action.type === '@@redux-undo/REDO') {
      saveDiffed(state.appId, state.list)
    }

    if (action.type === SET_TOOL) {
      return {
        ...state,
        activeTab: null,
      }
    }

    if (action.type === SELECT_PARENT) {
      const { objectId } = action

      return {
        ...state,
        selectedParent: objectId,
      }
    }

    if (action.type === PAN) {
      return {
        ...state,
        panning: action.value,
      }
    }

    if (action.type === SET_LIBRARY_GLOBAL) {
      const { appId } = state
      let { libraryGlobals } = state
      const { libraryName, componentName, changes } = action

      libraryGlobals = {
        ...state.libraryGlobals,
        [libraryName]: {
          ...state.libraryGlobals[libraryName],
          [componentName]: deepMerge(
            state.libraryGlobals[libraryName]?.[componentName],
            changes
          ),
        },
      }

      saveLibraryGlobals(appId, libraryGlobals)

      return { ...state, libraryGlobals }
    }

    return state
  }
)

// ACTIONS

export const requestData = appId => ({
  type: REQUEST_DATA,
  appId,
})

export const setData = (appId, data, app) => dispatch => {
  dispatch({
    type: SET_DATA,
    appId,
    data,
    app,
  })

  window.setTimeout(() => {
    dispatch({ type: '@@redux-undo/CLEAR_HISTORY' })
  }, 0)
}

export const createObject = (object, path = null, silent = false) => ({
  type: CREATE_OBJECT,
  object,
  path,
  silent,
  id: getId(),
})

export const updateObject =
  (id, object, undoGroupKey) => (dispatch, getState) =>
    dispatch({
      type: UPDATE_OBJECT,
      id,
      object,
      undoGroup: undoGroupKey && `${undoGroupKey}-${id}`,
      globalState: getState(),
    })

export const updateObjects = objects => (dispatch, getState) =>
  dispatch({
    type: UPDATE_OBJECTS,
    objects,
    globalState: getState(),
  })

export const changeObjectType = (id, newType) => ({
  type: CHANGE_OBJECT_TYPE,
  id,
  newType,
})

export const resizeObject =
  (id, object, skipSave = false, resetAccordion = false) =>
  (dispatch, getState) =>
    dispatch({
      type: RESIZE_OBJECT,
      id,
      object,
      skipSave,
      globalState: getState(),
      resetAccordion,
    })

export const positionObjects = (ids, offset, shiftKey, shouldSave = true) => ({
  type: POSITION_OBJECTS,
  ids,
  offset,
  shiftKey,
  shouldSave,
})

export const alignObjects = direction => ({
  type: ALIGN_OBJECTS,
  direction,
})

export const deleteObject = id => ({
  type: DELETE_OBJECT,
  id,
})

export const reorderObjects = (ids, dropTarget, options) => ({
  type: REORDER_OBJECTS,
  ids,
  dropTarget,
  options,
})

export const reorderObjectsMoveFirst = ids => ({
  type: REORDER_OBJECTS_MOVE_FIRST,
  ids,
})

export const reorderObjectsMoveLast = ids => ({
  type: REORDER_OBJECTS_MOVE_LAST,
  ids,
})

export const reorderObjectsMoveUp = ids => ({
  type: REORDER_OBJECTS_MOVE_UP,
  ids,
})

export const reorderObjectsMoveDown = ids => ({
  type: REORDER_OBJECTS_MOVE_DOWN,
  ids,
})

export const groupObjectsToList = ids => ({
  type: GROUP_OBJECTS_TO_LIST,
  ids,
})

export const groupObjects = ids => ({
  type: GROUP_OBJECTS,
  ids,
})

export const ungroupObjects = ids => ({
  type: UNGROUP_OBJECTS,
  ids,
})

export const setZoom = (scale, offset, relativeScale, relativeOffset) => ({
  type: ZOOM,
  scale,
  offset,
  relativeScale,
  relativeOffset,
})

export const resetZoom = () => ({ type: RESET_ZOOM })

export const selectParent = id => ({
  type: SELECT_PARENT,
  objectId: id,
})

export const setPan = value => (dispatch, getState) => {
  const state = getState()
  const panning = getPanning(state)

  if (value === true && panning === true) return false

  return dispatch({
    type: PAN,
    value,
  })
}

export const setLibraryGlobals = (libraryName, componentName, changes) => ({
  type: SET_LIBRARY_GLOBAL,
  libraryName,
  componentName,
  changes,
})

// SELECTORS

export const selectObjects = (state, ids = null) => {
  const list = state.editor.objects.present.list

  if (!ids) {
    return list
  }

  ids = uniqueElements(ids)

  return ids.map(id => getObject(list, getPath(state, id))).filter(o => o)
}

// Used by marquee selection
export const selectVisible = state => {
  return state.editor.objects.present.list.filter(o => !o.hidden)
}

export const selectObject = (state, id) => {
  const idComponents = id && typeof id === 'string' ? id.split('.') : undefined

  id = idComponents && idComponents.length > 0 ? idComponents[0] : id
  const path = getPath(state, id)
  const objects = selectObjects(state)

  return getObject(objects, path)
}

export const getMap = state => {
  return state.editor.objects.present.map
}

export const getPath = (state, objectId) => {
  return state.editor.objects.present.map[objectId]
}

const getSelectedSub = (objects, selection) => {
  const { map, list } = objects

  const result = selection.map(id => getObject(list, map[id]))

  const idMap = {}

  return result.filter(obj => {
    if (!obj) {
      return false
    }

    const { id } = obj

    if (idMap[id]) {
      return false
    }

    idMap[id] = true

    return true
  })
}

export const getSelectedObjects = state => {
  const { objects } = state.editor
  const selection = objects.present.selection

  return getSelectedSub(objects.present, selection)
}

export const getSelectedIds = state => {
  return state.editor.objects.present.selection
}

export const getZoom = ({ editor }) => editor.objects.present.zoom

export const getPage = state => {
  const { width, height, backgroundColor } = state.editor.objects.present

  return { width, height, backgroundColor }
}

export const getCurrentAppId = state => {
  const { appId } = state.editor.objects.present

  return appId
}

export const getObjectsOfType = (state, componentId, type) => {
  const { typeIndex } = state.editor.objects.present
  let index = (typeIndex[componentId] || {})[type]

  if (!componentId) {
    index = []

    for (const component of state.editor.objects.present.list) {
      index = index.concat((typeIndex[component.id] || {})[type] || [])
    }
  }

  return index || []
}

export const getLibraryInputs = (state, componentId, allowedDataTypes) => {
  const libraryObjects = getObjectsOfType(state, componentId, LIBRARY_COMPONENT)
  const app = state.apps.apps[getCurrentAppId(state)]

  // Results will have the form: { objectId, fieldId: [childComponent, prop] }
  let results = []

  const defaultDataType = dataTypes.TEXT

  for (const id of libraryObjects) {
    const obj = selectObject(state, id)

    if (!obj) continue

    const { libraryName, componentName } = obj
    const config = getAppComponent(app, libraryName, componentName)

    if (!config) continue

    // Loop through props
    for (const prop of config.props) {
      if (hasRole(prop, 'formValue')) {
        results.push({
          objectId: id,
          prop: [prop.name],
          dataType: prop.type || defaultDataType,
        })
      }
    }

    // Loop through child components
    for (const child of config.childComponents || []) {
      for (const prop of child.props) {
        if (hasRole(prop, 'formValue')) {
          results.push({
            objectId: id,
            prop: [child.name, prop.name],
            dataType: prop.type || defaultDataType,
          })
        }
      }
    }
  }

  if (allowedDataTypes) {
    results = results.filter(result => {
      return allowedDataTypes.includes(result.dataType)
    })
  }

  return results
}

export const getInputs = (state, componentId, objectId, objectType = INPUT) => {
  let inputs = []

  inputs = inputs.concat(
    getLibraryInputs(state, componentId, [dataTypes.TEXT, dataTypes.NUMBER])
  )

  inputs = inputs.concat(getObjectsOfType(state, componentId, objectType))

  let inputIds = inputs

  const list = state.editor.objects.present?.list

  inputIds = inputIds.map(id => {
    const path = getPath(state, id)

    const listParentIds = []

    for (let i = pathLength(path) - 1; i > 0; i -= 1) {
      const obj = getObject(list, subPath(path, pathLength(path) - i))

      if (!obj) continue
      if (obj.type === LIST) listParentIds.push(obj.id)
    }

    if (listParentIds.length === 0) return id

    return listParentIds.concat([id])
  })

  const objectListParents = []
  const path = getPath(state, objectId)

  for (let i = pathLength(path) - 1; i > 0; i -= 1) {
    const obj = getObject(list, subPath(path, pathLength(path) - i))

    if (!obj) continue
    if (obj.type === LIST) objectListParents.push(obj.id)
  }

  inputIds = inputIds.filter(id => {
    if (!Array.isArray(id)) return id
    const inputPath = id.slice(0, id.length - 1).join('.')
    const objectPath = objectListParents.join('.')

    return objectPath.startsWith(inputPath)
  })

  return inputIds
}

export const getLocationInputs = (state, componentId) => [
  ...getLibraryInputs(state, componentId, [dataTypes.LOCATION]),
  ...getObjectsOfType(state, componentId, LOCATION_INPUT),
]

export const getImagePickers = (state, componentId) => {
  let inputs = []

  inputs = inputs.concat(
    getLibraryInputs(state, componentId, [dataTypes.IMAGE])
  )

  inputs = inputs.concat(getObjectsOfType(state, componentId, IMAGE_UPLOAD))

  return inputs
}

export const getFilePickers = (state, componentId) => {
  let inputs = []

  inputs = inputs.concat(getLibraryInputs(state, componentId, [dataTypes.FILE]))
  inputs = inputs.concat(getObjectsOfType(state, componentId, FILE_UPLOAD))

  return inputs
}

export const getDatePickers = (state, componentId) => {
  let inputs = []

  inputs = inputs.concat(
    getLibraryInputs(state, componentId, [dataTypes.DATE, dataTypes.DATE_ONLY])
  )

  inputs = inputs.concat(getObjectsOfType(state, componentId, DATE_PICKER))

  return inputs
}

export const getSelects = (state, componentId, objectId) => {
  return getInputs(state, componentId, objectId, SELECT)
}

export const getLists = (state, componentId) => {
  return getObjectsOfType(state, componentId, LIST)
}

export const getCheckboxes = (state, componentId) => {
  return getLibraryInputs(state, componentId, [dataTypes.BOOLEAN])
}

export const getLoading = state => state.editor.objects.present.loading

export const getName = state => state.editor.objects.present.name

export const getComponent = (state, objectId) => {
  const path = getPath(state, objectId)

  if (!path) {
    return null
  }

  const componentPath = subPath(path, 1)
  const objects = selectObjects(state)
  const component = getObject(objects, componentPath)

  return component || null
}

export const getComponentId = (state, objectId) => {
  const component = getComponent(state, objectId)

  return (component && component.id) || null
}

export const getObjectPosition = (state, objectId) => {
  const object = selectObject(state, objectId)
  const component = getComponent(state, objectId)

  const { x, y, width, height } = object

  if (object.type === COMPONENT) {
    return object
  }

  return {
    width,
    height,
    x: x + component.x,
    y: y + component.y,
  }
}

export const getDataBinding = (state, objectId) => {
  const object = selectObject(state, objectId)

  return object && object.dataBindings && object.dataBindings[0]
}

export const getActions = (state, objectId) => {
  const object = selectObject(state, objectId)

  return (object && object.actions) || {}
}

export const getParentComponent = (state, objectId) => {
  const { list, map } = state.editor.objects.present

  if (objectId) {
    return getObject(list, getParentPath(map[objectId]))
  }

  return null
}

export const getPlatform = state => {
  const appId = getCurrentAppId(state)
  const app = state?.apps?.apps?.[appId]

  return app?.primaryPlatform || 'mobile'
}

export const getYOffset = state => {
  const platform = getPlatform(state)

  if (platform === 'web') {
    return 20
  }

  return 0
}

export const getSelectedParent = state => {
  const { selectedParent } = state.editor.objects.present

  if (!selectedParent) {
    return null
  }

  const objects = selectObjects(state, [selectedParent])

  return objects ? objects[0] : null
}

export const getPanning = state => {
  return state.editor.objects.present.panning
}

export const getLibraryGlobals = (state, libraryName, componentName) => {
  const globals = state.editor.objects.present.libraryGlobals

  return globals[libraryName]?.[componentName] || {}
}
